import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ElementRef,
    EventEmitter,
    Input,
    OnDestroy,
    OnInit,
    Output,
    Renderer2
} from '@angular/core';
import {CommonModule, NgForOf, NgIf} from '@angular/common';
import {FormsModule} from '@angular/forms';
import {MatFormFieldModule} from '@angular/material/form-field';
import {MatInputModule} from '@angular/material/input';
import {MatSelectModule} from '@angular/material/select';
import {MatIconModule} from '@angular/material/icon';
import {MatButtonModule} from '@angular/material/button';
import {QuillEditorComponent} from 'ngx-quill';
import {EditableQuillHtmlDirective} from '../../../../../core/directives/EditableQuillHtmlDirective';
import {QuillConfiguration, QuillFormats, setupClipboardInterceptors} from '../../../../../../environments/fixed-enum';
import {Subject, takeUntil} from 'rxjs';
import {RestFactoryService} from '../../../../../core/services/rest-factory.service';
import {FuseConfirmationService} from '../../../../../../@fuse/services/confirmation';
import Quill from 'quill';

@Component({
    selector: 'app-stories-tab',
    templateUrl: './stories-tab.component.html',
    styleUrls: ['./stories-tab.component.scss'],
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        CommonModule,
        NgIf,
        NgForOf,
        FormsModule,
        MatFormFieldModule,
        MatInputModule,
        MatSelectModule,
        MatIconModule,
        MatButtonModule,
        QuillEditorComponent,
        EditableQuillHtmlDirective,
    ],
})
export class StoriesTabComponent implements OnInit ,OnDestroy {
    @Input() storyGroup: any;
    @Input() destinationId: string;
    @Input() reviewMode: boolean = false;
    @Output() save = new EventEmitter<void>();

    selectedStory: any = null;
    newStoryTypeId: string = null;
    typingTimeout: any;
    quillModules: any = QuillConfiguration;
    quillFormats: any[] = QuillFormats;

    private _unsubscribeAll: Subject<any> = new Subject<any>();
    private API_POST_STORY = "/admin/destination/storyadd";
    private API_POST_STORY_IMAGE = "/admin/destination/storymainimageadd";

    constructor(
        private _restFactoryService: RestFactoryService,
        private _fuseConfirmationService: FuseConfirmationService,
        private _changeDetectorRef: ChangeDetectorRef,
        private _renderer: Renderer2,
        private _el: ElementRef
    ) {
    }

    ngOnInit() {
        if (this.storyGroup?.ar_items?.length > 0) {
            this.selectedStory = this.storyGroup.ar_items[0];
        }
    }

    ngOnDestroy(): void {
        this._unsubscribeAll.next(null);
        this._unsubscribeAll.complete();
    }

    selectStory(story: any): void {
        this.selectedStory = story;
    }

    onKeyupAutosave(): void {
        clearTimeout(this.typingTimeout);
        this.typingTimeout = setTimeout(() => {
            this.save.emit();
        }, 500);
    }

    onEditorCreated(quillInstance: Quill): void {
        setupClipboardInterceptors(quillInstance);
    }

    removeStory(storyToDelete: any, event: MouseEvent): void {
        event.stopPropagation();

        if (!storyToDelete) {
            return;
        }

        this._fuseConfirmationService.open({
            title: "Warning!",
            message: `The story "${storyToDelete.ds_name || 'Untitled'}" will be deleted!`,
            actions: {confirm: {show: true}, cancel: {show: true}},
            dismissible: true
        }).afterClosed().subscribe((result) => {
            if (result === "confirmed") {
                const index = this.storyGroup.ar_items.findIndex(item => item === storyToDelete);
                if (index > -1) {
                    this.storyGroup.ar_items.splice(index, 1);

                    if(this.selectedStory === storyToDelete) {
                        this.selectedStory = null;
                    }

                    this.save.emit();
                    this._changeDetectorRef.markForCheck();
                }
            }
        });
    }

    addStory(): void {
        const payload = new FormData();
        payload.append("id_storytype", this.newStoryTypeId);
        payload.append("id_destination", this.destinationId);

        this._restFactoryService.httpPostService(this.API_POST_STORY, payload)
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((response) => {
                this.storyGroup.ar_items.push(response.data);
                this.selectStory(response.data);
                this.newStoryTypeId = null;
                this._changeDetectorRef.markForCheck();
            });
    }

    onFileInputClick(storyId: string): void {
        const fileInput = this._el.nativeElement.querySelector(`#FileUpload${storyId}`);
        this._renderer.selectRootElement(fileInput).click();
    }

    onChangeFile(event: any, story: any): void {
        const file = (event.target as HTMLInputElement).files[0];
        if (!file) return;

        const payload = new FormData();
        payload.append("im_image", file);
        payload.append("id_destination", this.destinationId);
        payload.append("id_destinationstory", story.id_destinationstory);

        this._restFactoryService.httpPostService(this.API_POST_STORY_IMAGE, payload)
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((response) => {
                story.im_media = response.im_media;
                this._changeDetectorRef.markForCheck();
            });
    }

    public getStoryTypeName(storyTypeId: string): string {
        if (!this.storyGroup || !this.storyGroup.ar_storytypes) {
            return '';
        }
        const storyType = this.storyGroup.ar_storytypes.find(t => t.id_storytype === storyTypeId);
        return storyType ? storyType.ds_label : 'Unknown Type';
    }
}
